import { captureException, configureScope as sentryConfigureScope } from '@sentry/browser';
import { parse } from 'qs';
import * as ReactGA from 'react-ga';
import { IntercomWrapper } from 'src/services/intercom';
import {
  CLEAR_COOKIE_LIST,
  controlCookieSize,
  readCookie,
  readCookiesForService,
  readGAConnectorCookies,
  setCookie,
} from 'src/utils/Cookies';
import { initializeDatadog } from 'src/utils/Datadog';
import { getField } from 'src/utils/EntryUtil';
import { Environment } from 'src/utils/Environment';
import { fetchClientConfig } from 'src/utils/FetchClientConfig';
import { LocalStorage } from 'src/utils/LocalStorage';
import { LanguageCode } from 'src/utils/Locales';
import { CookieConfig } from './CookieConfig';
import { initializeGTM } from './gtm';
import {
  ANALYTICS_COOKIE_NAME,
  addUserPropertiesToHeap,
  initializeHeap,
  setupAnalyticsId,
  trackHeapEvent
} from './heap';
import { initializeTealium } from './tealium';

// export used to expose TRACKER_IDS to tests
export const TRACKER_IDS = {
  FD_TRACKER: { // FirstData
    type: 'GA',
    id: 'UA-73329301-1',
    name: 'FDTRACKER',
  },
};

export const AGENT_ID_COOKIE_NAME = 'agentId';
export const SF_PARTNER_ID_COOKIE_NAME = 'salesforcePartnerId';

// Extra trackers to send GA data to (gets setup in `initialize`)
const googleAnalyticsExtraTrackerNames: string[] = [];

// Cache the cookie config so we don't call contentful unnecessarily
let cachedCookieConfig: CookieConfig[] | undefined;

export { ReactGA };

export async function initialize(testMode: boolean = false) {

  // Initialize Clover's Google Tag Manager
  initializeGTM();

  // Initialize Clover's Tealium tag manager
  initializeTealium();

  initializeHeap();

  const trackers: ReactGA.Tracker[] = [
    { trackingId: Environment.getGAPropertyId() },
  ];

  // Configure and Init Datadog when env is development
  if (Environment.current.isCloverDevEnvironment || Environment.current.isCloverProdEnvironment
      || Environment.current.isCloverStagingEnvironment) {
    initializeDatadog();
  }

  if (Environment.current.isCloverProdEnvironment) {
    trackers.push({ trackingId: TRACKER_IDS.FD_TRACKER.id, gaOptions: { name: TRACKER_IDS.FD_TRACKER.name } });
    googleAnalyticsExtraTrackerNames.push(TRACKER_IDS.FD_TRACKER.name);
  }

  // Initialize GA Trackers
  ReactGA.initialize(trackers, { testMode, debug: Environment.current.isDev && !testMode });

  const hasChatDisabled = readCookie('referralType') === 'BC';
  IntercomWrapper.boot({
    ...(hasChatDisabled && { hide_default_launcher: true }),
  });

  cachedCookieConfig = undefined;
  try {
    const cookieConfig = await fetchContentfulCookieConfig();
    createCookies(cookieConfig);
    setAnalyticsPropertiesFromCookies(cookieConfig);
  } catch (err) {
    console.error('Error fetching cookie config from Contentful', err);
    const e = new Error(err.message);
    e.stack = err.stack;
    e.name = 'Error fetching cookie config from Contentful';
    captureException(e);
  }

  setupAnalyticsId()
  .then(clAnalyticsId => updateServicesWithAnalyticsId(clAnalyticsId))
  .catch (err => {
    captureException(`setupAnalyticsId failed with error: ${err}`);
  });

  // Google Analytics
  const gaconnector = window.gaconnector;
  if (gaconnector) { gaconnector.setCallback(() => controlCookieSize()); }
}

export function updateServicesWithAnalyticsId(clAnalyticsId: string) {
  setCookie(ANALYTICS_COOKIE_NAME, clAnalyticsId, 365 * 2);

  IntercomWrapper.update({
    analytics_id: clAnalyticsId,
  });

  sentryConfigureScope(scope => scope.setUser({ id: clAnalyticsId }));

  ReactGA.set({ userId: clAnalyticsId }, googleAnalyticsExtraTrackerNames);
}

export function outboundLink<T extends {label: string}>(args: T) {
  return new Promise(resolve => ReactGA.outboundLink(args, resolve as any, googleAnalyticsExtraTrackerNames));
}

export function trackEvent(eventAction: string, eventLabel?: string, eventValue?: number, data: object = {}) {
  ReactGA.event({
    category: 'Site',
    action: eventAction,
    label: eventLabel,
    value: eventValue,
  }, googleAnalyticsExtraTrackerNames);

  trackHeapEvent(eventAction, {
    ...data,
    eventLabel,
    eventValue,
  });

  IntercomWrapper.trackEvent(eventAction, {
    ...data,
    value: eventLabel,
    eventValue,
  });
}

export function trackPage() {
  ReactGA.set({ page: window.location.pathname }, googleAnalyticsExtraTrackerNames);
  ReactGA.pageview(window.location.pathname, googleAnalyticsExtraTrackerNames);
  IntercomWrapper.update({
    current_page_url: window.location.pathname,
  });
}

// GA, Google Analytics, Heap
export function makeMiscInfoJsonString(cookieConfig: CookieConfig[], additionalFields = {}) {
  const utmCampaign = readCookie('utm_campaign') || 'Clover_Commerce_WhereToBuy';

  const miscInfoJson = {
    utm_campaign: utmCampaign,
    HeapId: readCookie('clAnalyticsId'),
    acquiring_page_url: `${window.location.host}${window.location.pathname}`,
    ...additionalFields,
    ...readCookiesForService(cookieConfig, 'miscInfo'),
    ...readGAConnectorCookies(),
  };

  return JSON.stringify(miscInfoJson, (key, value) => {
    if (value !== null) { return value; }
  });
}

export function sendGACookiesToAnalytics() {
  addUserPropertiesToAnalytics({
    ...readGAConnectorCookies(true),
  });
}

export function addUserPropertiesToAnalytics(properties: object) {
  addUserPropertiesToHeap(properties);

  IntercomWrapper.update(properties);

  if (window.Invoca) {
    try {
      window.Invoca.PNAPI.run();
    } catch (err) {
      let msg = err.message;
      if (err.name === 'SecurityError') {
        msg += ' Cookies are most likely disabled on the browser.';
      }
      const e = new Error(msg);
      e.stack = err;
      e.name = 'InvocaLoadError';
      captureException(e);
      console.error('Unable to run Invoca', e);
    }
  }
}

export async function fetchContentfulCookieConfig() : Promise<CookieConfig[]> {
  if (cachedCookieConfig) { return Promise.resolve(cachedCookieConfig); }

  const clientConfig = await fetchClientConfig(LanguageCode.ENGLISH);
  if (!clientConfig) return Promise.reject({ message: 'Error retrieving Client Config from Contentful.' });

  const cookieConfig = getField(clientConfig, 'cookieConfig');
  if (!cookieConfig) return Promise.reject({ message: 'Error retrieving Cookie Config in Client Config from Contentful.' });

  cachedCookieConfig = cookieConfig;
  return cookieConfig;
}

function createCookies(cookieConfig : CookieConfig[]) {
  const params = parse(window.location.search, { ignoreQueryPrefix: true });

  cookieConfig.map(e => e.cookieName).forEach(cookieName => {
    const queryParamValue = params[cookieName] as string;
    if (!queryParamValue) return;
    if (CLEAR_COOKIE_LIST.includes(cookieName)) return;
    if (AGENT_ID_COOKIE_NAME === cookieName) { LocalStorage.trySetItem(AGENT_ID_COOKIE_NAME, queryParamValue); }

    setCookie(cookieName, queryParamValue, 90);
  });
}

function setAnalyticsPropertiesFromCookies(cookieConfig : CookieConfig[]) {
  sendGACookiesToAnalytics();

  addUserPropertiesToHeap(readCookiesForService(cookieConfig, 'heap'));
  setTimeout(() => { IntercomWrapper.update(readCookiesForService(cookieConfig, 'intercom')); }, 10000);
}

export function getUrlAgentIdFromLocalStorage() {
  const agentId = LocalStorage.tryGetItem(AGENT_ID_COOKIE_NAME);
  return agentId;
}
